/**
 * Proximity Toggle Component
 * ==========================
 * Toggle button showing proximity status with controls.
 * Shows "● Proximity On" when active, allowing quick control.
 */

import React, { useState } from 'react';
import { Radar, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { useProximity } from '../context/ProximityContext';

const ProximityToggle = ({ compact = false }) => {
  const {
    proximityEnabled,
    visibilityMode,
    permissionStatus,
    locationAccuracy,
    enableProximity,
    disableProximity,
    pauseVisibility,
    resumeVisibility
  } = useProximity();

  const [showControls, setShowControls] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleToggle = async () => {
    setLoading(true);
    if (proximityEnabled) {
      await disableProximity();
    } else {
      await enableProximity();
    }
    setLoading(false);
  };

  const handlePause = async (minutes) => {
    setLoading(true);
    await pauseVisibility(minutes);
    setLoading(false);
    setShowControls(false);
  };

  const handleResume = async () => {
    setLoading(true);
    await resumeVisibility();
    setLoading(false);
  };

  // Compact version - just the status indicator
  if (compact) {
    return (
      <button
        onClick={() => setShowControls(!showControls)}
        data-testid="proximity-toggle-compact"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          borderRadius: '20px',
          border: 'none',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: '600',
          transition: 'all 0.3s ease',
          ...(proximityEnabled ? {
            background: visibilityMode === 'paused' 
              ? 'rgba(255, 193, 7, 0.15)' 
              : 'rgba(76, 175, 80, 0.15)',
            color: visibilityMode === 'paused' ? '#FFC107' : '#4CAF50',
            border: `1px solid ${visibilityMode === 'paused' ? 'rgba(255, 193, 7, 0.4)' : 'rgba(76, 175, 80, 0.4)'}`
          } : {
            background: 'rgba(138, 115, 85, 0.15)',
            color: '#8A7355',
            border: '1px solid rgba(138, 115, 85, 0.3)'
          })
        }}
      >
        <span style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: proximityEnabled 
            ? (visibilityMode === 'paused' ? '#FFC107' : '#4CAF50') 
            : '#8A7355',
          boxShadow: proximityEnabled && visibilityMode !== 'paused'
            ? '0 0 8px rgba(76, 175, 80, 0.6)' 
            : 'none'
        }} />
        {proximityEnabled 
          ? (visibilityMode === 'paused' ? 'Paused' : 'Proximity On')
          : 'Proximity Off'}
      </button>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Main Toggle Button */}
      <button
        onClick={() => setShowControls(!showControls)}
        data-testid="proximity-toggle"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 16px',
          borderRadius: '12px',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600',
          width: '100%',
          transition: 'all 0.3s ease',
          ...(proximityEnabled ? {
            background: visibilityMode === 'paused' 
              ? 'linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 152, 0, 0.1) 100%)'
              : 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(56, 142, 60, 0.1) 100%)',
            color: visibilityMode === 'paused' ? '#FFC107' : '#4CAF50',
            border: `1px solid ${visibilityMode === 'paused' ? 'rgba(255, 193, 7, 0.4)' : 'rgba(76, 175, 80, 0.4)'}`
          } : {
            background: 'rgba(138, 115, 85, 0.1)',
            color: '#8A7355',
            border: '1px solid rgba(138, 115, 85, 0.3)'
          })
        }}
      >
        <Radar size={20} style={{ 
          animation: proximityEnabled && visibilityMode === 'active' ? 'pulse 2s infinite' : 'none' 
        }} />
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{ fontWeight: '600' }}>
            {proximityEnabled 
              ? (visibilityMode === 'paused' ? 'Visibility Paused' : 'Proximity Discovery On')
              : 'Proximity Discovery Off'}
          </div>
          {proximityEnabled && (
            <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '2px' }}>
              Your exact location is hidden
            </div>
          )}
        </div>
        {showControls ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {/* Expanded Controls */}
      {showControls && (
        <div 
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '8px',
            background: 'rgba(26, 15, 10, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(165, 124, 51, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            zIndex: 100,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }}
          data-testid="proximity-controls"
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <span style={{ color: '#E6D7C3', fontSize: '14px', fontWeight: '600' }}>
              Proximity Discovery
            </span>
            <label className="toggle-switch" style={{ margin: 0 }}>
              <input
                type="checkbox"
                checked={proximityEnabled}
                onChange={handleToggle}
                disabled={loading}
                data-testid="proximity-main-toggle"
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <p style={{ 
            color: '#8A7355', 
            fontSize: '12px', 
            marginBottom: '16px',
            lineHeight: '1.5'
          }}>
            {proximityEnabled 
              ? 'You can discover members within 60 feet who also have this enabled.'
              : 'Be visible to participating members within 60 feet.'}
          </p>

          {proximityEnabled && (
            <>
              {/* Visibility Mode */}
              <div style={{ 
                borderTop: '1px solid rgba(165, 124, 51, 0.2)',
                paddingTop: '12px',
                marginBottom: '12px'
              }}>
                <div style={{ 
                  color: '#A57C33', 
                  fontSize: '11px', 
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: '8px'
                }}>
                  Visibility
                </div>
                
                {visibilityMode === 'paused' ? (
                  <button
                    onClick={handleResume}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#FFF',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Resume Visibility
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handlePause(30)}
                      disabled={loading}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: 'rgba(255, 193, 7, 0.15)',
                        border: '1px solid rgba(255, 193, 7, 0.3)',
                        borderRadius: '8px',
                        color: '#FFC107',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Pause 30m
                    </button>
                    <button
                      onClick={() => handlePause(60)}
                      disabled={loading}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: 'rgba(255, 193, 7, 0.15)',
                        border: '1px solid rgba(255, 193, 7, 0.3)',
                        borderRadius: '8px',
                        color: '#FFC107',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Pause 1h
                    </button>
                  </div>
                )}
              </div>

              {/* Location Status */}
              {locationAccuracy && (
                <div style={{ 
                  borderTop: '1px solid rgba(165, 124, 51, 0.2)',
                  paddingTop: '12px'
                }}>
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: '#8A7355',
                    fontSize: '11px'
                  }}>
                    <span>Location accuracy</span>
                    <span style={{
                      color: locationAccuracy < 20 ? '#4CAF50' : 
                             locationAccuracy < 50 ? '#FFC107' : '#F44336'
                    }}>
                      {Math.round(locationAccuracy)}m
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {permissionStatus === 'denied' && (
            <div style={{
              marginTop: '12px',
              padding: '10px',
              background: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              borderRadius: '8px'
            }}>
              <p style={{ 
                color: '#F44336', 
                fontSize: '12px',
                marginBottom: '8px'
              }}>
                Location permission is required for Proximity Discovery.
              </p>
              <button
                onClick={() => {
                  // Attempt to open settings (may not work on all browsers)
                  alert('Please enable location permission in your browser settings.');
                }}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  border: '1px solid rgba(244, 67, 54, 0.5)',
                  borderRadius: '6px',
                  color: '#F44336',
                  fontSize: '11px',
                  cursor: 'pointer'
                }}
              >
                Open Settings
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProximityToggle;
